import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  BadgeDollarSign,
  Building2,
  CheckCircle2,
  Home,
  Layers,
  Loader2,
  LogOut,
  MapPin,
  Plus,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { firestore } from '@config/firebase';
import { useLandlordDashboard } from '@hooks/useLandlordDashboard';
import { Property, PropertyStatus } from '../../types/property.types';
import { getErrorMessage } from '@utils/errors';

type StatusFilter = PropertyStatus | 'all';

const MyProperties: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userRole, logout } = useAuth();
  const { properties, loading, error } = useLandlordDashboard(currentUser?.uid);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filtered = useMemo(() => {
    return [...properties]
      .filter((property) => (statusFilter === 'all' ? true : property.status === statusFilter))
      .filter((property) => {
        if (!search.trim()) return true;
        const term = search.toLowerCase();
        return (
          property.title.toLowerCase().includes(term) ||
          property.address.city.toLowerCase().includes(term) ||
          property.propertyType.toLowerCase().includes(term)
        );
      })
      .sort(
        (a, b) =>
          (b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0) -
          (a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0)
      );
  }, [properties, statusFilter, search]);

  const summary = useMemo(() => {
    const active = properties.filter((p) => p.status !== 'draft').length;
    const available = properties.filter((p) => p.status === 'available').length;
    const rented = properties.filter((p) => p.status === 'rented').length;
    const avgRent =
      active > 0
        ? Math.round(
            properties
              .filter((p) => p.status !== 'draft')
              .reduce((sum, p) => sum + (p.monthlyRent || 0), 0) / active
          )
        : 0;
    const totalViews = properties.reduce((sum, p) => sum + ((p as any).views ?? 0), 0);
    return { active, available, rented, avgRent, totalViews };
  }, [properties]);

  const handleStatusUpdate = async (propertyId: string, nextStatus: PropertyStatus) => {
    if (!currentUser) return;
    setUpdatingId(propertyId);
    setUpdateError(null);
    try {
      const ref = doc(firestore, 'properties', propertyId);
      await updateDoc(ref, {
        status: nextStatus,
        isActive: nextStatus !== 'draft',
        updatedAt: serverTimestamp(),
      });
    } catch (err: unknown) {
      console.error('Error updating property status', err);
      setUpdateError(getErrorMessage(err, 'Could not update status. Check permissions or try again.'));
    } finally {
      setUpdatingId(null);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="card p-8 max-w-lg w-full text-center space-y-4">
          <h1 className="text-3xl font-extrabold text-text-primary">Landlord access only</h1>
          <p className="text-text-secondary">Sign in to view and manage your listings.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/login')}>
              Sign in
            </button>
            <button className="btn-secondary w-full sm:w-auto" onClick={() => navigate('/signup?role=landlord')}>
              Create landlord account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (userRole && userRole !== 'landlord') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="card p-8 max-w-lg w-full text-center space-y-4">
          <h1 className="text-3xl font-extrabold text-text-primary">Switch to landlord</h1>
          <p className="text-text-secondary">This workspace is dedicated to landlord listings.</p>
          <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/login')}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-32 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-12 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <header className="bg-white/80 backdrop-blur-md border border-gray-100 shadow-lg rounded-2xl px-3 sm:px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/landlord/dashboard')}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center shadow-lg hover:shadow-xl transition-all shrink-0 cursor-pointer"
                aria-label="Go to dashboard home"
              >
                <Home className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-extrabold text-text-primary truncate">My Properties</h1>
                <p className="text-xs text-text-muted">Manage your listings</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button className="btn-secondary py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2" onClick={() => navigate('/landlord/dashboard')}>
                <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Dashboard
              </button>
              <button className="btn-primary py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2" onClick={() => navigate('/landlord/create-property')}>
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> New
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="border-l-4 border-red-400 bg-red-50 text-red-700 px-4 py-3 rounded-xl shadow-sm">
            <p className="font-semibold">Could not load properties</p>
            <p className="text-sm">Firestore replied with: {error}. Check permissions or indexes.</p>
          </div>
        )}
        {updateError && (
          <div className="border-l-4 border-amber-400 bg-amber-50 text-amber-800 px-4 py-3 rounded-xl shadow-sm">
            <p className="font-semibold">Status update failed</p>
            <p className="text-sm">{updateError}</p>
          </div>
        )}

        <div className="card p-4 sm:p-6 bg-white/90">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-extrabold text-text-primary mb-1">Overview</h2>
            <p className="text-text-secondary text-sm">
              {summary.active} active • {summary.totalViews} total views
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            <StatCard title="Active listings" value={summary.active} helper={`${summary.available} available`} icon={<Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />} accent="from-primary-200 to-primary-300" />
            <StatCard title="Rented" value={summary.rented} helper="Currently occupied" icon={<CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />} accent="from-accent-purple to-primary-300" />
            <StatCard title="Avg. rent" value={summary.avgRent > 0 ? `R${summary.avgRent.toLocaleString()}` : '—'} helper="Across active listings" icon={<BadgeDollarSign className="w-4 h-4 sm:w-5 sm:h-5" />} accent="from-accent-blue to-accent-teal" />
            <StatCard title="Total views" value={summary.totalViews.toLocaleString()} helper="Lifetime" icon={<MapPin className="w-4 h-4 sm:w-5 sm:h-5" />} accent="from-primary-300 to-accent-purple" />
          </div>
        </div>

        <div className="card p-4 sm:p-6 bg-white/90">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-text-primary">All Properties</h3>
              <p className="text-text-secondary text-sm">{properties.length} total</p>
            </div>
            <button
              className="btn-outline py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 shrink-0"
              onClick={() => navigate('/landlord/create-property')}
            >
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Add
            </button>
          </div>

          <div className="flex flex-col gap-3 mb-4">
            <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
              {(['all', 'available', 'pending', 'rented', 'draft'] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(status)}
                  aria-pressed={statusFilter === status}
                  className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm font-semibold transition-all duration-150 whitespace-nowrap ${
                    statusFilter === status
                      ? 'border-primary-200 bg-primary-50 text-primary-300 shadow-sm'
                      : 'border-gray-200 bg-white text-text-secondary hover:border-primary-100'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
            <input
              aria-label="Search properties by title, city, or type"
              className="input text-sm sm:text-base"
              placeholder="Search by title, city, or type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="h-32 flex items-center justify-center text-text-secondary">Loading your properties...</div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center space-y-2">
              <h4 className="text-lg font-semibold text-text-primary">No properties found</h4>
              <p className="text-text-secondary text-sm">
                {properties.length === 0
                  ? 'Create your first listing to start gathering inquiries.'
                  : 'Try a different filter or search term.'}
              </p>
              <button className="btn-primary" onClick={() => navigate('/landlord/create-property')}>
                Create listing
              </button>
            </div>
          ) : (
            <div className="grid gap-3">
              {filtered.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onStatusChange={handleStatusUpdate}
                  updating={updatingId === property.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Logout Button */}
        <div className="mt-8 pb-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: string | number;
  helper: string;
  icon: React.ReactNode;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, helper, icon, accent }) => (
  <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
    <div className="flex items-center justify-between mb-2 sm:mb-3">
      <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">{title}</p>
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br ${accent} text-white flex items-center justify-center shadow-lg shrink-0`}>
        {icon}
      </div>
    </div>
    <p className="text-xl sm:text-2xl font-extrabold text-text-primary truncate">{value}</p>
    <p className="text-xs text-text-muted mt-1 line-clamp-1">{helper}</p>
  </div>
);

interface PropertyCardProps {
  property: Property;
  updating: boolean;
  onStatusChange: (propertyId: string, nextStatus: PropertyStatus) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, updating, onStatusChange }) => {
  const statusStyles: Record<PropertyStatus, string> = {
    available: 'bg-green-50 text-green-700 border-green-100',
    pending: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    rented: 'bg-blue-50 text-blue-700 border-blue-100',
    draft: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  return (
    <div className="border border-gray-100 rounded-xl p-3 sm:p-4 bg-white/60 hover:bg-white transition-colors shadow-sm hover:shadow-md">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
            <h4 className="text-base sm:text-lg font-bold text-text-primary truncate">{property.title}</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${statusStyles[property.status]} shrink-0 self-start`}>
              {property.status}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-text-secondary line-clamp-2">{property.description}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-xs sm:text-sm text-text-secondary">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary-50 text-primary-300 border border-primary-100">
              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="truncate">{property.address.city}</span>
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 text-purple-700 border border-purple-100">
              <Layers className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {property.propertyType}
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-accent-blue/10 text-accent-blue border border-accent-blue/30">
              R{property.monthlyRent.toLocaleString()}/mo
            </div>
          </div>
        </div>
        <div className="flex sm:flex-col gap-2 text-xs sm:text-sm text-text-secondary">
          <div className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-primary-50 text-primary-300 border border-primary-100 font-semibold whitespace-nowrap">
            {property.views ?? 0} views
          </div>
          <div className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-accent-purple/10 text-accent-purple border border-accent-purple/30 font-semibold whitespace-nowrap">
            {(property as any).inquiries ?? 0} inquiries
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-3">
        <div className="text-xs text-text-muted">
          <span>
            Available from{' '}
            {property.availableFrom?.toDate
              ? property.availableFrom.toDate().toLocaleDateString()
              : 'TBC'}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-secondary font-semibold">Update status:</span>
          {(['available', 'pending', 'rented', 'draft'] as PropertyStatus[]).map((status) => (
            <button
              key={status}
              type="button"
              disabled={updating || property.status === status || !property.id}
              onClick={() => property.id && onStatusChange(property.id, status)}
              className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all duration-150 ${
                property.status === status
                  ? 'border-primary-200 bg-primary-50 text-primary-300'
                  : 'border-gray-200 bg-white text-text-secondary hover:border-primary-100'
              } ${updating ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {updating && property.status !== status ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
              {status}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyProperties;
